import React, { FC, useState } from 'react';
import { useKeycloak } from '@react-keycloak/web';
import { Box, Typography } from '@mui/material';
import { useAppDispatch } from '../../../state';
import { issueVc } from '../../../state/slices/issuevc';
import { credentialOffers } from './credentialOffers';
import { CredentialRow, RowState } from './CredentialRow';

const ERROR_MESSAGE = 'Could not generate the QR code. Please try again.';

export const CredentialsPage: FC = () => {
    const dispatch = useAppDispatch();
    const {keycloak} = useKeycloak();
    const [rows, setRows] = useState<Record<string, RowState>>({});

    function updateRow(templateId: string, patch: Partial<RowState>) {
        setRows(prev => ({
            ...prev,
            [templateId]: {...(prev[templateId] ?? {loading: false, visible: false}), ...patch},
        }));
    }

    function handleToggle(templateId: string) {
        const current = rows[templateId];

        // Already showing -> hide (keep the cached offer URL for instant re-show).
        if (current?.visible) {
            updateRow(templateId, {visible: false});
            return;
        }

        // Offer already issued -> just show it again (reuses the same offer).
        if (current?.oidcUrl) {
            updateRow(templateId, {visible: true});
            return;
        }

        // First time -> issue the credential offer, then show its QR.
        updateRow(templateId, {loading: true, error: undefined});
        dispatch(issueVc({jwtToken: keycloak.token!, templateId, callbackUrl: ''}))
            .then((data) => {
                if (data.type.includes('fulfilled') && data.payload?.oidcUrl) {
                    updateRow(templateId, {loading: false, visible: true, oidcUrl: data.payload.oidcUrl});
                } else {
                    updateRow(templateId, {loading: false, error: ERROR_MESSAGE});
                }
            })
            .catch(() => {
                updateRow(templateId, {loading: false, error: ERROR_MESSAGE});
            });
    }

    return (
        <Box>
            <Typography sx={{fontSize: '48px', fontFamily: 'DM Serif Display', color: '#000000', marginBottom: '8px'}}>
                Demo credentials
            </Typography>
            <Typography sx={{fontSize: '14px', color: '#000000', marginBottom: '28px'}}>
                Press &quot;Add to wallet&quot; and scan the QR code with your wallet to receive the credential.
            </Typography>
            {credentialOffers.map(offer => (
                <CredentialRow
                    key={offer.templateId}
                    offer={offer}
                    state={rows[offer.templateId] ?? {loading: false, visible: false}}
                    onToggle={handleToggle}
                />
            ))}
        </Box>
    );
};
